import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Switch,
  FormControlLabel,
  InputLabel,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { updateNotification, Notification } from '../../services/notificationService';
import { uploadFile } from '../../firebase';

interface EditNotificationDialogProps {
  open: boolean;
  notification: Notification | null;
  onClose: (updated?: boolean) => void;
}

const EditNotificationDialog: React.FC<EditNotificationDialogProps> = ({ 
  open, 
  notification,
  onClose 
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [formData, setFormData] = useState({
    id: 0,
    title: '',
    description: '',
    img: '',
    recipients: '',
    status: 'activo' as 'activo' | 'inactivo',
  });

  useEffect(() => {
    if (notification) {
      setFormData({
        id: notification.id,
        title: notification.title,
        description: notification.description,
        img: notification.img,
        recipients: notification.recipients ? notification.recipients.join(', ') : '',
        status: notification.status as 'activo' | 'inactivo',
      });
      setImagePreview(notification.img);
      setImageFile(null);
      setError('');
    }
  }, [notification]);

  // Clean up object URLs on unmount or when imagePreview changes
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        status: checked ? 'activo' : 'inactivo'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleImageFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageFile(file);
    }
  };

  const handleImageFile = useCallback((file: File) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Formato de archivo no válido. Use JPG, PNG o WebP.');
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1024 * 1024) {
      setError('El archivo es demasiado grande. El tamaño máximo permitido es 1MB.');
      return;
    }

    // Validate image dimensions (1:1 aspect ratio)
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      if (img.width !== img.height) {
        setError('La imagen debe tener una relación de aspecto 1:1 (ancho = alto)');
        URL.revokeObjectURL(objectUrl);
      } else {
        setError('');
        setImageFile(file);
        // Clean up previous preview if it was a blob URL
        if (imagePreview && imagePreview.startsWith('blob:')) {
          URL.revokeObjectURL(imagePreview);
        }
        setImagePreview(objectUrl);
        console.log('Nueva imagen seleccionada:', file.name);
      }
    };
    
    img.onerror = () => {
      setError('Error al cargar la imagen');
      URL.revokeObjectURL(objectUrl);
    };
    
    img.src = objectUrl;
  }, [imagePreview]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      let imageUrl = formData.img;
      
      // If a new image was selected, upload it to Firebase Storage
      if (imageFile) {
        const timestamp = Date.now();
        const fileExtension = imageFile.name.split('.').pop();
        const fileName = `notification_${timestamp}.${fileExtension}`;
        const storagePath = `notifications/${fileName}`;
        
        enqueueSnackbar('Subiendo nueva imagen...', { variant: 'info' });
        imageUrl = await uploadFile(imageFile, storagePath);
        console.log('Nueva imagen subida:', imageUrl);
      }
      
      const updateData = {
        id: formData.id,
        title: formData.title,
        description: formData.description,
        img: imageUrl,
        recipients: formData.recipients,
        status: formData.status,
      };
      
      console.log('Actualizando notificación con:', updateData);
      await updateNotification(updateData);
      
      enqueueSnackbar('Notificación actualizada correctamente', { variant: 'success' });
      
      // Clean up
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      
      onClose(true);
    } catch (error: any) {
      console.error('Error updating notification:', error);
      setError(error.response?.data?.message || 'Error al actualizar la notificación');
      enqueueSnackbar('Error al actualizar la notificación', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.title && formData.description && (formData.img || imageFile);

  if (!notification) return null;

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Editar Notificación</Typography>
          <IconButton onClick={() => onClose()} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box display="flex" flexDirection="column" gap={3} mt={1}>
          <TextField
            name="title"
            label="Título"
            value={formData.title}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            required
          />
          <TextField
            name="description"
            label="Descripción"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            required
          />
          
          {/* Image Upload Section */}
          <Box>
            <InputLabel sx={{ mb: 1, fontWeight: 500 }}>Imagen de la notificación *</InputLabel>
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('edit-file-upload')?.click()}
              sx={{
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragging ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
                minHeight: 200,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <input
                id="edit-file-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              {imagePreview ? (
                <Box>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Vista previa"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 180,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      mx: 'auto',
                      display: 'block'
                    }}
                  />
                  <Button
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById('edit-file-upload')?.click();
                    }}
                    sx={{ mt: 1 }}
                  >
                    Cambiar imagen
                  </Button>
                </Box>
              ) : (
                <>
                  <CloudUploadIcon 
                    color={isDragging ? 'primary' : 'action'} 
                    sx={{ fontSize: 48, mb: 1 }} 
                  />
                  <Typography variant="body2" color="textPrimary" gutterBottom>
                    {isDragging ? 'Suelta la imagen aquí' : 'Haz clic para seleccionar o arrastra una imagen'}
                  </Typography>
                  <Box mt={1}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Formato: Cuadrada (1:1)
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Formatos: WebP, PNG, JPG
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Tamaño máximo: 1MB
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Box>
          <TextField
            name="recipients"
            label="Destinatarios (opcional, separados por comas)"
            value={formData.recipients}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            helperText="Dejar vacío para enviar a todos los usuarios"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.status === 'activo'}
                onChange={handleChange}
                name="status"
                color="primary"
              />
            }
            label={formData.status === 'activo' ? 'Activo' : 'Inactivo'}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!isFormValid || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{
            '&:hover': {
              backgroundColor: '#C8102E',
            },
          }}
        >
          {loading ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditNotificationDialog;
